import mqtt from 'mqtt';

function randomClientSuffix() {
    const bytes = new Uint8Array(8);
    if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
        window.crypto.getRandomValues(bytes);
    } else {
        // Le suffixe évite seulement une collision de clientId ; ce n'est pas
        // un secret. Ce chemin sert notamment à l'environnement de test Jest.
        for (let index = 0; index < bytes.length; index += 1) {
            bytes[index] = Math.floor(Math.random() * 256);
        }
    }
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

function validateCredentials(credentials, sessionId) {
    const expectedTopic = `session/${sessionId}/events`;
    if (!credentials || typeof credentials !== 'object') {
        throw new Error('Identifiants MQTT absents');
    }
    if (credentials.topic !== expectedTopic) {
        throw new Error('Topic MQTT inattendu');
    }
    if (!credentials.username || !credentials.password || !credentials.clientIdPrefix) {
        throw new Error('Identifiants MQTT incomplets');
    }

    const websocketUrl = new URL(credentials.websocketUrl);
    if (!['ws:', 'wss:'].includes(websocketUrl.protocol)) {
        throw new Error('Transport MQTT non WebSocket');
    }
    if (window.location.protocol === 'https:' && websocketUrl.protocol !== 'wss:') {
        throw new Error('Transport MQTT non chiffré');
    }

    return { expectedTopic, websocketUrl: websocketUrl.toString() };
}

function payloadToString(payload) {
    if (typeof payload === 'string') return payload;
    if (payload instanceof Uint8Array) {
        // Les événements MQTT sont des chaînes JSON ou hexadécimales ASCII.
        return Array.from(payload, byte => String.fromCharCode(byte)).join('');
    }
    return String(payload);
}

/**
 * Ouvre une connexion MQTT strictement limitée au topic de la session.
 * MQTT.js gère la reconnexion et le réabonnement après une perte réseau.
 */
export function createSessionRealtimeClient({
    credentials,
    sessionId,
    onPayload,
    onStatus = () => {},
}) {
    const { expectedTopic, websocketUrl } = validateCredentials(credentials, sessionId);
    const client = mqtt.connect(websocketUrl, {
        clientId: `${credentials.clientIdPrefix}${randomClientSuffix()}`,
        username: credentials.username,
        password: credentials.password,
        protocolVersion: 4,
        clean: true,
        keepalive: 30,
        connectTimeout: 10_000,
        reconnectPeriod: 2_000,
        resubscribe: true,
    });

    client.on('connect', () => {
        client.subscribe(expectedTopic, { qos: 0 }, error => {
            if (error) {
                onStatus('subscription-error', error);
                return;
            }
            onStatus('subscribed');
        });
    });
    client.on('reconnect', () => onStatus('reconnecting'));
    client.on('offline', () => onStatus('offline'));
    client.on('error', error => onStatus('error', error));
    client.on('message', (topic, payload) => {
        if (topic !== expectedTopic) return;
        onPayload(payloadToString(payload));
    });

    return client;
}
